import type {
	CoursesAdminAssignMentorDto,
	CoursesAdminBlackoutAuditQueryDto,
	CoursesAdminSetOfficeHoursEnabledDto,
} from '@sportspredict/shared';
import type { Wire } from '../../types/common.types';
import { coursesApi } from './courses-base.api';
import type {
	AdminListCourseMentorAssignmentsOutput,
	AdminListStaffOutput,
	AdminAssignMentorInput,
	AdminAssignMentorOutput,
	AdminUnassignMentorInput,
	AdminUnassignMentorOutput,
	AdminSetOfficeHoursEnabledInput,
	AdminSetOfficeHoursEnabledOutput,
	AdminListBlackoutAuditInput,
	AdminListBlackoutAuditOutput,
} from '../../types/admin-office-hours.types';

const TAG = 'courses:admin-office-hours' as const;
const INVALIDATES = [TAG, 'courses:office-hours', 'courses:webinars', 'courses:dashboard'] as const;

export const adminOfficeHoursEndpoints = coursesApi.injectEndpoints({
	endpoints: (builder) => ({
		adminListCourseMentorAssignments: builder.query<AdminListCourseMentorAssignmentsOutput, void>({
			query: () => ({ url: 'courses/admin/office-hours/mentor-assignments' }),
			providesTags: [TAG],
		}),
		adminListStaff: builder.query<AdminListStaffOutput, void>({
			query: () => ({ url: 'courses/admin/office-hours/staff' }),
			providesTags: [TAG],
		}),
		adminAssignMentor: builder.mutation<AdminAssignMentorOutput, AdminAssignMentorInput>({
			query: (body) => ({
				url: 'courses/admin/office-hours/mentor-assignments',
				method: 'POST',
				body: body satisfies Wire<CoursesAdminAssignMentorDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminUnassignMentor: builder.mutation<AdminUnassignMentorOutput, AdminUnassignMentorInput>({
			query: ({ courseId, userId }) => ({
				url: `courses/admin/office-hours/mentor-assignments/${encodeURIComponent(courseId)}/${encodeURIComponent(userId)}`,
				method: 'DELETE',
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminSetOfficeHoursEnabled: builder.mutation<
			AdminSetOfficeHoursEnabledOutput,
			AdminSetOfficeHoursEnabledInput
		>({
			query: ({ courseId, ...body }) => ({
				url: `courses/admin/office-hours/courses/${encodeURIComponent(courseId)}/enabled`,
				method: 'PUT',
				body: body satisfies Wire<CoursesAdminSetOfficeHoursEnabledDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminListBlackoutAudit: builder.query<
			AdminListBlackoutAuditOutput,
			AdminListBlackoutAuditInput
		>({
			query: (params) => ({
				url: 'courses/admin/office-hours/blackout-audit',
				params: params satisfies Wire<CoursesAdminBlackoutAuditQueryDto>,
			}),
			providesTags: [TAG],
		}),
	}),
});

export const {
	useAdminListCourseMentorAssignmentsQuery,
	useLazyAdminListCourseMentorAssignmentsQuery,
	useAdminListStaffQuery,
	useLazyAdminListStaffQuery,
	useAdminAssignMentorMutation,
	useAdminUnassignMentorMutation,
	useAdminSetOfficeHoursEnabledMutation,
	useAdminListBlackoutAuditQuery,
	useLazyAdminListBlackoutAuditQuery,
} = adminOfficeHoursEndpoints;
